import { TAU, type Complex } from "./complex";
import { reconstruct, type FourierCoefficient } from "./dft";

export type Epicycle = Readonly<{
  center: Complex;
  tip: Complex;
  radius: number;
  frequency: number;
}>;
export const epicycles = (
  coefficients: readonly FourierCoefficient[],
  dc: Complex,
  t: number,
): Epicycle[] => {
  let center = dc;
  return coefficients.map((c) => {
    const angle = TAU * c.frequency * t + c.phase;
    const tip = {
      x: center.x + c.amplitude * Math.cos(angle),
      y: center.y + c.amplitude * Math.sin(angle),
    };
    const result = { center, tip, radius: c.amplitude, frequency: c.frequency };
    center = tip;
    return result;
  });
};

export const sampleTrail = (
  coefficients: readonly FourierCoefficient[],
  dc: Complex,
  resolution = 4096,
): Complex[] => {
  return Array.from({ length: resolution + 1 }, (_, i) => {
    const p = reconstruct(coefficients, i / resolution);
    return { x: p.x + dc.x, y: p.y + dc.y };
  });
};

/** The last vertex is the exact current chain tip, never a rounded sample index. */
export const visibleTrail = (
  samples: readonly Complex[],
  progress: number,
  exactTip: Complex,
): Complex[] => {
  const t = Math.max(0, Math.min(1, progress));
  const index = Math.floor(t * (samples.length - 1));
  return [...samples.slice(0, index + 1), exactTip];
};

export const toSvgPath = (points: readonly Complex[]) =>
  points
    .map((p, i) => {
      // Preserve full precision for the endpoint shared with the light marker.
      const end = i === points.length - 1;
      return `${i ? "L" : "M"}${end ? p.x : p.x.toFixed(5)},${end ? p.y : p.y.toFixed(5)}`;
    })
    .join(" ");
